import { ref } from 'vue'
import { config } from '../config'

export const isAuthenticated = ref(false)
export const user = ref(null)

const API_URL = config.getBackendUrl()

// Helper function to get all auth data
const getAuthData = () => {
  const data = localStorage.getItem('auth_data')
  return data ? JSON.parse(data) : null
}

// Helper function to set all auth data
const setAuthData = (data: {
  access_token: string
  id_token: string
  refresh_token?: string
  user: any
}) => {
  localStorage.setItem('auth_data', JSON.stringify(data))
}

// Helper function to remove all auth data
const removeAuthData = () => {
  localStorage.removeItem('auth_data')
}

// Helper function to get the stored token
const getToken = () => {
  const data = getAuthData()
  return data?.access_token || null
}

// Helper function to get the stored id token
const getIdToken = () => {
  const data = getAuthData()
  return data?.id_token || null
}

export const authService = {
  async login() {
    const redirectUri = window.location.origin + '/authorize'
    const response = await fetch(
      `${API_URL}/auth/login?redirect_uri=${encodeURIComponent(redirectUri)}`,
    )
    const data = await response.json()
    window.location.href = data.auth_url
  },

  async handleCallback(code: string) {
    const redirectUri = window.location.origin + '/authorize'
    const response = await fetch(
      `${API_URL}/auth/callback?code=${code}&redirect_uri=${encodeURIComponent(redirectUri)}`,
    )
    const data = await response.json()

    // Store all auth data in a single object
    if (data.access_token && data.id_token) {
      setAuthData({
        access_token: data.access_token,
        id_token: data.id_token,
        refresh_token: data.refresh_token,
        user: data,
      })
    }
    isAuthenticated.value = true
    user.value = data
  },

  async logout() {
    try {
      const token = getToken()
      // First call our backend signout endpoint
      await fetch(`${API_URL}/auth/signout`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      // Clear local state
      removeAuthData()
      isAuthenticated.value = false
      user.value = null

      // Get the Cognito logout URL and redirect to it
      const redirectUri = window.location.origin
      const response = await fetch(
        `${API_URL}/auth/logout?redirect_uri=${encodeURIComponent(redirectUri)}`,
      )
      const data = await response.json()
      window.location.href = data.logout_url
    } catch (error) {
      console.error('Error during logout:', error)
      removeAuthData()
      isAuthenticated.value = false
      user.value = null
      window.location.href = '/'
    }
  },

  async getCurrentUser() {
    try {
      const id_token = getIdToken()
      if (!id_token) {
        console.log('Current user: anonymous')
        return null
      }

      const response = await fetch(`${API_URL}/auth/user`, {
        headers: {
          Authorization: `Bearer ${id_token}`,
        },
      })

      if (!response.ok) {
        throw new Error('Failed to get current user from token')
      }

      const data = await response.json()

      // Update the stored user data
      const authData = getAuthData()
      if (authData) {
        setAuthData({
          ...authData,
          user: data,
        })
      }
      console.log('Current user: ' + data.username)
      isAuthenticated.value = true
      user.value = data

      // set username if needed
      if (data.username && data.username.startsWith('google_')) {
        window.location.href = '/set-username'
      }

      return data
    } catch (error) {
      console.error('Error getting current user:', error)
      removeAuthData()
      isAuthenticated.value = false
      user.value = null
      return null
    }
  },

  async completeGame(
    finalScore: number,
    finalGrid: any | null,
    puzzleSize: number,
    puzzle_nb: number,
    hints_used: number = 0,
    time_spent: number = 0,
    state: any,
    infinite_mode: boolean = false,
  ) {
    try {
      const token = getIdToken()
      const response = await fetch(`${API_URL}/game/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          final_score: finalScore.toString(),
          final_grid: finalGrid,
          puzzle_size: puzzleSize,
          puzzle_nb: puzzle_nb,
          hints_used: hints_used,
          time_spent: time_spent,
          state: state,
          infinite_mode: infinite_mode,
        }),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.detail || 'Failed to store game result')
      }

      const data = await response.json()
      console.log('Game result stored successfully')
      return data
    } catch (error) {
      console.error('Error storing game result:', error)
      throw error
    }
  },

  async setUsername(username: string) {
    try {
      console.log('Hello from setUsername')
      const token = getToken()
      const response = await fetch(`${API_URL}/auth/set-username`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ username }),
      })

      if (!response.ok) {
        throw new Error('Failed to set username')
      }

      // After setting username, refresh the token to get updated user info
      const authData = getAuthData()
      if (authData?.refresh_token) {
        const refreshResponse = await fetch(
          `${API_URL}/auth/refresh?refresh_token=${encodeURIComponent(authData.refresh_token)}&id_token=${encodeURIComponent(authData.id_token)}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
          },
        )

        if (refreshResponse.ok) {
          const newTokens = await refreshResponse.json()
          setAuthData({
            ...authData,
            access_token: newTokens.accessToken,
            id_token: newTokens.idToken,
          })
        }
      }

      // Update local user data
      const currentUser = await this.getCurrentUser()
      return currentUser
    } catch (error) {
      console.error('Error setting username:', error)
      throw error
    }
  },
}
